// Live presence (who's online right now).
//
// Two delivery paths, mirroring the chat + notifications hooks:
//   1. WebSocket: realtime.subscribe('presence.online' / 'presence.offline')
//      pushes per-user transitions as they happen.
//   2. REST snapshot: useQuery hits GET /v5/presence on mount and as a 60s
//      polling fallback for when the WS is closed (background tab, etc.).
//
// State shape: a Map<userId, PresenceUser> in TanStack Query's cache. Both
// delivery paths converge on it — the WS handler patches the map, the REST
// query replaces it. Last writer wins; the WS frames are eventually consistent
// with the snapshot (any drift is corrected on the next poll).
//
// SUBSCRIPTION OWNERSHIP (audit X06): the WS subscription + the polling query
// are mounted EXACTLY ONCE, app-level, via usePresenceSubscription() in the
// authed layout. The read hooks (useIsOnline / usePresence) are pure cache
// reads with no effect and no queryFn — so dropping N avatars onto a list does
// NOT spawn N subscriptions or N polling queries (the old useIsOnline → full
// usePresence() shape did, registering 2N realtime handlers and firing a
// refetch on every presence frame).

import { useEffect, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { realtime } from '../realtime';
import { useFallbackPollInterval } from './realtime';

export interface PresenceUser {
  id: string;
  username: string;
  // Server's last-seen-active time for this user, as epoch ms. Null/undefined
  // means "online but we have no activity stamp" → treated as away (orange).
  lastActivityAt?: number | null;
}

// presence.online / presence.activity frames both carry an optional `at`
// (server UTC ISO string); presence.offline carries only userId.
interface PresenceFrame {
  userId: string;
  at?: string;
}

interface PresenceCache {
  // Indexed by user id for O(1) lookup from useIsOnline.
  byId: Map<string, PresenceUser>;
}

/** Three rendered states for the avatar dot. */
export type PresenceState = 'offline' | 'away' | 'active';

// "Active" (green) window: a user is green for this long after their last
// activity stamp. Matches the product spec (3 min) plus a 30s grace margin so
// the boundary between a continuously-active user's heartbeats (sent every
// ~2.5min) never momentarily computes "away". See activityHeartbeat.ts.
const ACTIVE_WINDOW_MS = 3 * 60_000 + 30_000; // 3.5 min

// How often the shared clock ticks to re-evaluate active→away transitions.
// Coarse on purpose: the only thing that changes purely with time is a user
// crossing the idle threshold, and 30s granularity is imperceptible for that
// while keeping the wakeups (one timer for the whole app) cheap.
const CLOCK_TICK_MS = 30_000;

const KEY = ['presence', 'set'] as const;

function parseAt(at: string | undefined): number | null {
  if (!at) return null;
  const ms = Date.parse(at);
  return Number.isNaN(ms) ? null : ms;
}

function emptyCache(): PresenceCache {
  return { byId: new Map() };
}

async function fetchSnapshot(signal?: AbortSignal): Promise<PresenceUser[]> {
  const { data, error } = await api.GET('/v5/presence', { signal });
  if (error) throw error;
  return (data?.online ?? []).map((u) => ({
    id: u.id,
    username: u.username,
    lastActivityAt: parseAt(u.lastActivityAt ?? undefined),
  }));
}

/**
 * Reconcile a server snapshot INTO the existing cache instead of replacing it.
 * The snapshot is authoritative for membership (who is online), but we carry a
 * known username forward when the snapshot's row has one and so does the cache
 * — and, crucially, we never transit through an empty/stubbed state for a user
 * who was already fully known. This is what stops the "Varga shows Unknown,
 * then my own card disappears" flicker on a peer reload: the snapshot rebuild
 * keeps every still-present user's existing username rather than re-stubbing.
 */
function reconcile(prev: PresenceCache | undefined, snapshot: PresenceUser[]): PresenceCache {
  const prevById = prev?.byId;
  const byId = new Map<string, PresenceUser>();
  for (const u of snapshot) {
    // Prefer the snapshot's username; fall back to the cached one if the
    // snapshot's is blank (shouldn't happen server-side, but defensive).
    const known = prevById?.get(u.id);
    // Activity: take whichever timestamp is newer. A WS presence.activity frame
    // may have advanced the cached value past what this (slightly stale)
    // snapshot reports, so never let the snapshot regress a fresher stamp.
    const snapAt = u.lastActivityAt ?? null;
    const knownAt = known?.lastActivityAt ?? null;
    const lastActivityAt =
      snapAt != null && knownAt != null ? Math.max(snapAt, knownAt) : (snapAt ?? knownAt);
    byId.set(u.id, {
      id: u.id,
      username: u.username || known?.username || '',
      lastActivityAt,
    });
  }
  return { byId };
}

/**
 * Mount the presence data source ONCE, app-level (authed layout). Owns the
 * polling snapshot query AND the realtime subscription; every other presence
 * read just consumes the cache this populates. Returns the query so the caller
 * can render a loading/error state if it wants (the layout ignores it).
 *
 * The WebSocket frames (`presence.online` / `presence.offline`) carry only the
 * userId — when a user we don't already have in the cache comes online we add a
 * stub `{ id, username: '' }`. To turn that stub into a real username we do a
 * COALESCED refetch: a single snapshot ~800ms after a burst of new-user frames,
 * instead of one refetch per frame. That keeps X06's anti-storm property (N
 * arrivals → 1 refetch, not N) while still resolving "Unknown" quickly rather
 * than waiting up to 60s for the poll. Frames for users already in the cache,
 * and offline frames, never schedule a refetch.
 */
export function usePresenceSubscription(): UseQueryResult<PresenceCache> {
  const qc = useQueryClient();
  // Poll cadence: 60s when the WS is DOWN (the only live source then), but we
  // ALSO keep a slow 30s authoritative poll while the WS is UP. The WS only
  // delivers TRANSITIONS, so a viewer who mounts after others are already
  // online never receives a frame for them — the mount snapshot is their only
  // way to learn about them, and a reconcile poll self-heals a missed/raced
  // one.
  const fallbackInterval = useFallbackPollInterval();
  const refetchInterval = fallbackInterval === false ? 30_000 : fallbackInterval;

  const query = useQuery({
    queryKey: KEY,
    // Reconcile the snapshot INTO the live cache (merge, don't replace): keep
    // known usernames for users still present, so a peer reload never flashes
    // anyone — including self — through an empty/stubbed render.
    queryFn: async ({ signal, client }) => {
      const snapshot = await fetchSnapshot(signal);
      const prev = client.getQueryData<PresenceCache>(KEY);
      return reconcile(prev, snapshot);
    },
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 30_000,
    // A failed/aborted reconcile (transient 401 during token refresh, nav
    // abort) must NOT blank a populated list — keep the last good cache.
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    // Coalesce the username-refill triggered by genuinely-new online frames
    // into one trailing snapshot ~800ms after a burst. The refill reconciles
    // (merges) rather than invalidates, so it patches the new user's username
    // in place WITHOUT the whole list transiting through a refetch — which is
    // what made self's card briefly vanish on a peer reload.
    let refillTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleUsernameRefill = () => {
      if (refillTimer) return; // a refill is already pending for this burst
      refillTimer = setTimeout(() => {
        refillTimer = undefined;
        void (async () => {
          try {
            const snapshot = await fetchSnapshot();
            qc.setQueryData<PresenceCache>(KEY, (prev) => reconcile(prev, snapshot));
          } catch {
            // Refill is best-effort; the 30s reconcile poll will catch up.
          }
        })();
      }, 800);
    };

    // A peer RELOAD fires presence.offline (old socket) immediately followed by
    // presence.online (new socket) ~milliseconds later. Removing on the offline
    // frame then re-adding on the online frame loses the user's known username
    // (re-stub → "Unknown" flash) and churns the list. So defer every removal
    // by a short grace window; an online frame for the same user within it
    // CANCELS the pending removal — the row is never deleted, so the online
    // handler's "already present with username" branch makes the reload a true
    // no-op. A genuine departure has no following online frame, so it removes
    // after the grace delay.
    const pendingRemovals = new Map<string, ReturnType<typeof setTimeout>>();
    const cancelRemoval = (userId: string) => {
      const t = pendingRemovals.get(userId);
      if (t) {
        clearTimeout(t);
        pendingRemovals.delete(userId);
      }
    };

    const offOnline = realtime.subscribe('presence.online', (payload) => {
      const p = payload as PresenceFrame;
      if (!p?.userId) return;
      cancelRemoval(p.userId); // reload round-trip: keep the existing row
      const at = parseAt(p.at);
      let needsUsername = false;
      qc.setQueryData<PresenceCache | undefined>(KEY, (prev) => {
        const cur = prev ?? emptyCache();
        const existing = cur.byId.get(p.userId);
        // Newer activity stamp wins (online implies fresh activity).
        const lastActivityAt = at != null ? at : (existing?.lastActivityAt ?? null);
        // Already present WITH a known username (a reload whose removal we just
        // cancelled, or a user the snapshot already named): keep the row but
        // still refresh activity so the dot goes/stays green on (re)connect.
        if (existing && existing.username) {
          if (lastActivityAt === existing.lastActivityAt) return cur;
          const next = new Map(cur.byId);
          next.set(p.userId, { ...existing, lastActivityAt });
          return { byId: next };
        }
        const next = new Map(cur.byId);
        // New (or a bare stub): keep any username we already had, else stub.
        next.set(p.userId, { id: p.userId, username: existing?.username ?? '', lastActivityAt });
        needsUsername = true;
        return { byId: next };
      });
      if (needsUsername) scheduleUsernameRefill();
    });
    // Activity heartbeat from a peer: refresh their last-activity stamp so the
    // dot stays/turns green. The user is already online (the server only emits
    // this for an online user), so we patch in place — and if we somehow don't
    // have them yet (raced ahead of their online frame), stub them in and let
    // the username refill fill the name.
    const offActivity = realtime.subscribe('presence.activity', (payload) => {
      const p = payload as PresenceFrame;
      if (!p?.userId) return;
      cancelRemoval(p.userId);
      const at = parseAt(p.at);
      if (at == null) return;
      let needsUsername = false;
      qc.setQueryData<PresenceCache | undefined>(KEY, (prev) => {
        const cur = prev ?? emptyCache();
        const existing = cur.byId.get(p.userId);
        if (existing) {
          // Only advance forward; ignore an out-of-order frame.
          if (existing.lastActivityAt != null && at <= existing.lastActivityAt) return cur;
          const next = new Map(cur.byId);
          next.set(p.userId, { ...existing, lastActivityAt: at });
          return { byId: next };
        }
        const next = new Map(cur.byId);
        next.set(p.userId, { id: p.userId, username: '', lastActivityAt: at });
        needsUsername = true;
        return { byId: next };
      });
      if (needsUsername) scheduleUsernameRefill();
    });
    const offOffline = realtime.subscribe('presence.offline', (payload) => {
      const p = payload as PresenceFrame;
      if (!p?.userId) return;
      if (pendingRemovals.has(p.userId)) return; // already scheduled
      const timer = setTimeout(() => {
        pendingRemovals.delete(p.userId);
        qc.setQueryData<PresenceCache | undefined>(KEY, (prev) => {
          if (!prev?.byId.has(p.userId)) return prev;
          const next = new Map(prev.byId);
          next.delete(p.userId);
          return { byId: next };
        });
      }, 1500); // grace window comfortably covers a reload's reconnect
      pendingRemovals.set(p.userId, timer);
    });
    return () => {
      offOnline();
      offOffline();
      offActivity();
      if (refillTimer) clearTimeout(refillTimer);
      for (const t of pendingRemovals.values()) clearTimeout(t);
    };
  }, [qc]);

  return query;
}

/**
 * Read-only access to the presence cache populated by usePresenceSubscription.
 * No queryFn (the app-level subscription owns fetching) — this just observes
 * the shared cache entry, so it's safe to call from any number of components
 * (e.g. one per avatar in a list) without spawning subscriptions or fetches.
 */
export function usePresence(): UseQueryResult<PresenceCache> {
  return useQuery<PresenceCache>({
    queryKey: KEY,
    // No queryFn: rely on usePresenceSubscription to populate/refresh. enabled:
    // false keeps this hook from ever initiating a fetch on its own. queryFn is
    // typed-but-unreachable to satisfy the generic without ever running.
    queryFn: () => emptyCache(),
    enabled: false,
  });
}

/**
 * Lightweight read for places that only need to know about one user. Reads the
 * shared cache via usePresence (no effect, no fetch) — dropping this onto N
 * list rows is O(1) per row and registers zero subscriptions.
 */
export function useIsOnline(userId: string | null | undefined): boolean {
  const q = usePresence();
  if (!userId) return false;
  return q.data?.byId.has(userId) ?? false;
}

// ── Shared presence clock ───────────────────────────────────────────────────
// A user's dot can change colour with NO incoming frame: when they stop
// interacting, their green window simply lapses and they become "away". To
// reflect that we must re-render on a timer — but one timer for the WHOLE app,
// not one per avatar. This module-level external store ticks every CLOCK_TICK_MS
// while (and only while) at least one usePresenceState consumer is mounted, and
// all consumers re-evaluate together off that single tick.
const presenceClock = (() => {
  const subscribers = new Set<() => void>();
  let tick = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  const start = () => {
    if (timer != null) return;
    timer = setInterval(() => {
      tick++;
      for (const fn of subscribers) fn();
    }, CLOCK_TICK_MS);
  };
  const stop = () => {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
  };

  return {
    subscribe(fn: () => void): () => void {
      subscribers.add(fn);
      start();
      return () => {
        subscribers.delete(fn);
        if (subscribers.size === 0) stop();
      };
    },
    // Snapshot is the tick counter — a new number forces dependent re-renders.
    getSnapshot: () => tick,
    getServerSnapshot: () => 0,
  };
})();

/**
 * Compute a user's three-state presence — 'offline' (not connected), 'away'
 * (connected but idle > 3 min, orange) or 'active' (interacted within the last
 * ~3 min, green) — from the shared presence cache + the shared clock.
 *
 * Reads the cache via usePresence (no fetch) and subscribes to the single
 * app-wide presence clock so the active→away transition is reflected without an
 * incoming frame. Dropping this onto N list rows registers zero network work
 * and shares ONE timer across all rows.
 */
export function usePresenceState(userId: string | null | undefined): PresenceState {
  const q = usePresence();
  // Re-render this row whenever the shared clock ticks (every CLOCK_TICK_MS) so
  // a lapsed green window flips to orange even with no frame in flight.
  useSyncExternalStore(presenceClock.subscribe, presenceClock.getSnapshot, presenceClock.getServerSnapshot);

  if (!userId) return 'offline';
  const user = q.data?.byId.get(userId);
  if (!user) return 'offline';
  const at = user.lastActivityAt;
  if (at != null && Date.now() - at < ACTIVE_WINDOW_MS) return 'active';
  // Online (in the cache) but no recent activity stamp → logged in but idle.
  return 'away';
}

/**
 * Cheap count-only fetch for the navbar badge. Hits /v5/presence/count
 * which sidesteps serialising the full GUID list.
 */
export function useOnlineCount() {
  // Poll only while the WS is down; presence frames keep the set live otherwise.
  const refetchInterval = useFallbackPollInterval();
  return useQuery({
    queryKey: ['presence', 'count'],
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET('/v5/presence/count', { signal });
      if (error) throw error;
      return { count: Number(data?.count ?? 0) };
    },
    refetchInterval,
    staleTime: 30_000,
  });
}
